import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { doc, getDoc, collection, getDocs, addDoc, setDoc } from 'firebase/firestore'

import Button from '../Button'
import TextField from '../Form/TextField'
import DropdownField from '../Form/DropdownField'
import Columns from '../Columns'
import ContextMenu from '../ContextMenu'
import Spinner from '../Spinner'
import { useError, useForm } from '../../hooks'
import { db, getEnvClaim } from '../../firebase'

export default function ({ id, organizationId }) {

  const navigate = useNavigate()
  const setError = useError()
  const [getFieldProps, formData, formError] = useForm()

  const [organization, setOrganization] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const [organizations, setOrganizations] = useState(null)
  const [loading, setLoading] = useState(true)

  // 🔹 Fetch Firestore data
  useEffect(() => {
    async function fetchData() {
      try {
        // Get organization
        const orgRef = doc(db, 'organizations', organizationId)
        const orgSnap = await getDoc(orgRef)

        // Get user role
        const roleRef = doc(db, 'organizations', organizationId, 'user-roles', id)
        const roleSnap = await getDoc(roleRef)

        // Get organizations list.
        //
        // NOT an unfiltered list. The organizations rule authorises purely on
        // the document id (`platformAdmin() || boundTo(orgId)`), and Firestore
        // proves a list query from its CONSTRAINTS, not from the documents it
        // would return — so a bare getDocs on the collection can never satisfy
        // it and is refused outright. Read the reachable ones by id instead.
        //
        // A platform admin genuinely may list them all, so that branch stays.
        const claim = await getEnvClaim()
        let orgItems
        if (claim.platformAdmin === true) {
          const orgsSnap = await getDocs(collection(db, 'organizations'))
          orgItems = orgsSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        } else {
          const reachable = [claim.orgId, ...(claim.boundOrgIds || [])].filter(Boolean)
          const docs = await Promise.all(
            [...new Set(reachable)].map(oid => getDoc(doc(db, 'organizations', oid)))
          )
          orgItems = docs.filter(d => d.exists()).map(d => ({ id: d.id, ...d.data() }))
        }

        setOrganization(orgSnap.exists() ? { id: orgSnap.id, ...orgSnap.data() } : null)
        setUserRole(roleSnap.exists() ? { id: roleSnap.id, ...roleSnap.data() } : null)
        setOrganizations({ items: orgItems })

        setLoading(false)
      } catch (err) {
        setError(err)
        setLoading(false)
      }
    }

    fetchData()
  }, [organizationId, id, setError])

  if (loading || !organization || !userRole || !organizations) {
    return <Spinner />
  }

  async function handleSubmit(e) {
    e.preventDefault()

    const { targetOrganizationId } = formData

    try {
      // 🔹 Copy user role to new organization
      const newRoleId = `${Date.now()}`
      await setDoc(
        doc(db, 'organizations', targetOrganizationId, 'user-roles', newRoleId),
        {
          ...userRole,
          id: newRoleId,
          createdAt: new Date().toISOString()
        }
      )

      navigate(
        `/user-roles/${newRoleId}?organizationId=${targetOrganizationId}`
      )
    } catch (err) {
      if (err.message === 'validation_error') {
        formError(err.props)
      } else {
        setError(err)
      }
    }
  }

  return (
    <div>
      <ContextMenu
        renderTitle={() => (
          <h1 className='title'>Copy role "{userRole.name}"</h1>
        )}
      />
      <div className='box'>
        <h2 className='subtitle'>Details</h2>
        <form onSubmit={handleSubmit}>
          <Columns>
            <TextField
              label='Source Organization'
              value={organization.name}
              readOnly={true}
            />
            <TextField
              label='Role Name'
              value={userRole.name}
              readOnly={true}
            />
          </Columns>
          <Columns>
            <DropdownField
              name='targetOrganizationId'
              label='Target Organization'
              {...getFieldProps()}
            >
              <option />
              {organizations.items
                .filter(({ id }) => id !== organizationId)
                .map(({ id, name }) => (
                  <option value={id} key={id}>
                    {name}
                  </option>
                ))}
            </DropdownField>
          </Columns>
          <div className='flex justify-end'>
            <Button type='submit'>Copy</Button>
          </div>
        </form>
      </div>
    </div>
  )
}
